import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Crown, Loader2, Building2, HeartPulse, Car, Gift } from "lucide-react";
import SubscriptionStatusCard from "../components/SubscriptionStatusCard";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VERTICAL_PLANS } from "../lib/institutional";

const VERTICAL_ICONS = { Building2, HeartPulse, Car };

const PAID = ["business", "pro", "premium", "imoveis_pro", "medicos_pro", "auto_pro"];

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  const [sub, setSub] = useState(null);
  const { user, setUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {}); }, []);
  const loadSub = () => api.get("/subscriptions/me").then(({ data }) => setSub(data.subscription)).catch(() => setSub(null));
  useEffect(() => { if (user) loadSub(); else setSub(null); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [params] = useSearchParams();
  const payParam = params.get("pagar");
  const autoPaid = React.useRef(false);
  useEffect(() => {
    if (payParam && !user && !authLoading) { navigate(`/login?next=${encodeURIComponent(`/planos?pagar=${payParam}`)}`); return; }
    if (!payParam || !user || !sub || autoPaid.current || !PAID.includes(payParam)) return;
    autoPaid.current = true;
    setLoading(payParam);
    checkout(payParam).catch(() => { toast.error("Não foi possível abrir o pagamento."); setLoading(null); });
  }, [payParam, user, sub, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const priceOf = (key) => plans.find((p) => p.key === key)?.price_num || 0;
  const isCurrent = (key) => sub && sub.plan === key && ["trialing", "active", "payment_due"].includes(sub.status);
  const canTrial = !user || sub?.can_trial;

  const ctaLabel = (p) => {
    if (p.price_num === 0) return "Começar grátis";
    if (p.key === "enterprise") return "Falar com vendas";
    if (isCurrent(p.key)) return sub.status === "payment_due" ? "Pagar agora" : "Plano atual";
    if (canTrial) return "Testar 30 dias grátis";
    if (sub && PAID.includes(sub.plan) && ["trialing", "active"].includes(sub.status)) {
      const a = priceOf(p.key); const b = priceOf(sub.plan);
      return a > b ? "Fazer upgrade" : a < b ? "Fazer downgrade" : "Trocar de plano";
    }
    return "Assinar com Pix ou cartão";
  };

  const checkout = async (plan) => {
    const { data } = await api.post("/payments/preferences", { plan });
    if (data.checkout_url) window.location.assign(data.checkout_url);
    else throw new Error("no url");
  };

  const handleSelect = async (p, { payNow = false } = {}) => {
    if (p.key === "free") { navigate(user ? "/feed" : "/cadastro"); return; }
    if (p.key === "enterprise") { toast("Fale com nosso time", { description: "Envie um e-mail para contato@guiadejampa.ai" }); return; }
    if (!user) { toast("Crie sua conta para testar grátis", { description: "30 dias sem cobrança. Cancele quando quiser." }); navigate("/cadastro"); return; }
    if (isCurrent(p.key) && sub.status !== "payment_due") return;
    setLoading(p.key);
    try {
      if (payNow || (isCurrent(p.key) && sub.status === "payment_due")) { await checkout(p.key); return; }
      if (sub?.can_trial) {
        const { data } = await api.post("/subscriptions/trial", { plan: p.key });
        setUser(data.user); setSub(data.subscription);
        toast.success("Teste grátis ativado!", { description: `Plano ${data.subscription.plan_name} liberado por 30 dias, sem cobrança.` });
        setLoading(null);
        return;
      }
      const { data } = await api.post("/subscriptions/change", { plan: p.key });
      if (data.mode === "checkout" && data.checkout_url) { window.location.assign(data.checkout_url); return; }
      if (data.user) setUser(data.user);
      if (data.subscription) setSub(data.subscription);
      toast.success("Plano alterado com sucesso.");
      setLoading(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao processar o plano");
      setLoading(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancelar o plano e voltar para o gratuito? Seus dados e histórico serão mantidos.")) return;
    setLoading("cancel");
    try {
      const { data } = await api.post("/subscriptions/change", { plan: "free" });
      setUser(data.user); setSub(data.subscription);
      toast.success("Plano cancelado.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao cancelar"); }
    finally { setLoading(null); }
  };

  return (
    <div className="space-y-10">
      <div className="text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold mb-2">
          <Crown className="w-3.5 h-3.5" /> Planos
        </span>
        <h1 className="font-heading font-bold text-2xl md:text-3xl">Escolha seu plano</h1>
        <p className="text-sm text-muted-foreground mt-1">Do morador ao negócio premium. Pague com Pix ou cartão.</p>
        {canTrial && (
          <p data-testid="plans-trial-banner" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-700 px-4 py-1.5 text-sm font-semibold">
            <Gift className="w-4 h-4" /> 30 dias grátis em qualquer plano pago · sem cobrança inicial
          </p>
        )}
      </div>

      <SubscriptionStatusCard sub={sub} busy={!!loading} onCancel={cancel}
        onPay={() => handleSelect({ key: sub.plan }, { payNow: true })} />

      <div className="grid md:grid-cols-2 gap-4">
        {plans.filter((p) => !p.vertical).map((p, i) => (
          <motion.div key={p.key} data-testid={`plan-${p.key}`}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className={`rounded-3xl border p-6 flex flex-col ${p.highlight ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border bg-card"}`}>
            {isCurrent(p.key) && <span data-testid={`plan-current-${p.key}`} className="self-start rounded-full bg-success text-white text-[11px] font-bold px-3 py-1 mb-3">SEU PLANO</span>}
            {p.highlight && !isCurrent(p.key) && <span className="self-start rounded-full bg-primary text-primary-foreground text-[11px] font-bold px-3 py-1 mb-3">MAIS POPULAR</span>}
            <h3 className="font-heading font-bold text-xl">{p.name}</h3>
            <div className="mt-2 mb-4">
              <span className="font-heading font-extrabold text-3xl">{p.price}</span>
              {p.price_num > 0 && <span className="text-sm text-muted-foreground">/mês</span>}
            </div>
            <ul className="space-y-2.5 flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-success mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <button
              data-testid={`plan-select-${p.key}`}
              disabled={loading === p.key || (isCurrent(p.key) && sub.status !== "payment_due")}
              onClick={() => handleSelect(p)}
              className={`mt-5 rounded-full py-3 font-semibold transition-transform active:scale-95 flex items-center justify-center gap-2 disabled:opacity-60 ${
                p.highlight ? "bg-primary text-primary-foreground" : "border border-border hover:bg-muted"
              }`}>
              {loading === p.key ? <Loader2 className="w-4 h-4 animate-spin" /> : ctaLabel(p)}
            </button>
            {canTrial && p.price_num > 0 && user && (
              <button data-testid={`plan-paynow-${p.key}`} onClick={() => handleSelect(p, { payNow: true })} className="mt-2 text-xs text-muted-foreground underline underline-offset-2">ou assinar agora com Pix/cartão</button>
            )}
          </motion.div>
        ))}
      </div>

      {/* VERTICAL PLANS — Imóveis, Médicos, Auto */}
      <div className="pt-4">
        <div className="text-center mb-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold uppercase tracking-widest mb-2">
            Planos por vertical
          </span>
          <h2 className="font-heading font-bold text-xl md:text-2xl">Anuncie no seu segmento</h2>
          <p className="text-sm text-muted-foreground mt-1">Verticais dedicadas com até 50 anúncios/mês + 3 impulsionamentos grátis.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {VERTICAL_PLANS.map((p, i) => {
            const Icon = VERTICAL_ICONS[p.icon] || Building2;
            return (
              <motion.div
                key={p.key}
                data-testid={`plan-${p.key}`}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
                className="relative rounded-3xl overflow-hidden border border-border bg-card p-6 flex flex-col"
              >
                <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${p.accent}`} />
                <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${p.accent} flex items-center justify-center text-white shadow-md`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-bold text-lg mt-3">{p.name}</h3>
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{p.tag}</p>
                <div className="mt-2 mb-4">
                  <span className="font-heading font-extrabold text-3xl">{p.price}</span>
                  <span className="text-sm text-muted-foreground">{p.period}</span>
                </div>
                <ul className="space-y-2 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-success mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`plan-select-${p.key}`}
                  disabled={loading === p.key || (isCurrent(p.key) && sub.status !== "payment_due")}
                  onClick={() => handleSelect({ key: p.key, price_num: priceOf(p.key) || 1 })}
                  className={`mt-5 rounded-full py-3 font-semibold text-white bg-gradient-to-r ${p.accent} shadow-md disabled:opacity-60 flex items-center justify-center gap-2`}
                >
                  {loading === p.key ? <Loader2 className="w-4 h-4 animate-spin" /> : (canTrial || isCurrent(p.key) || sub?.status === "trialing" || sub?.status === "active" ? ctaLabel({ ...p, price_num: priceOf(p.key) || 1 }) : p.cta)}
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground">Pagamento processado com segurança pela InfinitePay. No teste grátis, nenhuma cobrança é feita antes de 30 dias.</p>
    </div>
  );
}
